import { useState } from 'react';
import { Button, Tab, Tabs } from '@mui/material';
import DriverSelector from '@components/AssignDriver/DriverSelector';
import TractorSelector from '@components/AssignDriver/TractorSelector';
import BrokerDataGrid from '@components/DataGrid/Broker/Grid';
import { PopupContentProps } from '@forms/Components/Dropdowns/ButtonDropdown';
import { useFetchDriverTractors } from '@hooks/useDriverTractors';
import ApplicationSpinner from '@/Infrastructure/Components/Loading/ApplicationSpinner';
import { DriverDropdownMode } from '@forms/Fields/Driver.types';
import SimpleDataGrid from '@components/DataGrid/Generic/SimpleDataGrid';
import { useFetchSalesPeopleQuery } from '@api/Trinium/Endpoints/Salepeople/slice';
import useCurrentTerminal from '@hooks/useCurrentTerminal';
import { salepeopleColumnDefinition } from '@components/DataGrid/types';
import { SalesPerson } from '@api/Trinium/Endpoints/Salepeople/types';

import styles from './ButtonContent.module.css';

/**
 *	Driver "Dropdown" Content. Mode configuration object contains settings to apply what values / types are available for selection.
 *	Tabs separate different entity types that can be selected as a "Supplier". Driver / Broker / Salesperson
 *	There is also a Tractor Tab to allow finding a Driver by the Tractor.
 */
export default function ButtonListContent({
  onChange,
  closeMenu,
  mode
}: PopupContentProps & {
  mode?: DriverDropdownMode;
}) {
  /**
   * Using string in place of number to prevent MUI default behavior of using the index'ed child
   * allowing more granular control such as conditional rendered tabs, etc.
   */
  const [tabIndex, setTabIndex] = useState('0');

  const { driversWithTractors, tractorsWithDrivers, isLoading } = useFetchDriverTractors();

  return (
    <>
      <Tabs
        value={tabIndex}
        onChange={(_, newValue: string) => {
          setTabIndex(newValue);
        }}
        className={styles.tabs}>
        {mode?.Drivers.enabled && (
          <Tab
            label='By Driver'
            id='assgn-driver-tab-1'
            aria-controls='assgn-driver-tab-panel-1'
            className={styles.tab}
            value='0'
          />
        )}
        {mode?.Tractors.enabled && (
          <Tab
            label='By Truck'
            id='assgn-driver-tab-2'
            aria-controls='assgn-driver-tab-panel-2'
            className={styles.tab}
            value='1'
          />
        )}
        {mode?.Carriers.enabled && (
          <Tab
            label='By Broker'
            id='assgn-driver-tab-3'
            aria-controls='assgn-driver-tab-panel-3'
            className={styles.tab}
            value='2'
          />
        )}
        {mode?.Salespeople.enabled && (
          <Tab
            label='By Sales'
            id='assgn-driver-tab-4'
            aria-controls='assgn-driver-tab-panel-4'
            className={styles.tab}
            value='3'
          />
        )}
        <Button
          variant='outlined'
          className={styles['close-btn']}
          onClick={() => {
            closeMenu();
          }}>
          Close
        </Button>
      </Tabs>
      <div
        id='assgn-driver-tab-panel-1'
        role='tabpanel'
        hidden={tabIndex !== '0'}
        aria-labelledby='assgn-driver-tab-1'
        className={styles['grid-container']}>
        {tabIndex === '0' && (
          <DriverSelector
            options={driversWithTractors}
            onChange={onChange}
            legRequiresHazmatCertification={mode?.requiresHazmat}
            disableRowSelectValidation={mode?.Drivers.itemOptions?.allowAll}
            disableSlick={mode?.driverOnly}
            isLoading={isLoading}
          />
        )}
      </div>
      <div
        id='assgn-driver-tab-panel-2'
        role='tabpanel'
        hidden={tabIndex !== '1'}
        aria-labelledby='assgn-driver-tab-2'
        className={styles['grid-container']}>
        {tabIndex === '1' && (
          <TractorSelector
            options={tractorsWithDrivers}
            legRequiresHazmatCertification={mode?.requiresHazmat}
            onChange={onChange}
            isLoading={isLoading}
          />
        )}
      </div>
      <div
        id='assgn-driver-tab-panel-3'
        role='tabpanel'
        hidden={tabIndex !== '2'}
        aria-labelledby='assgn-driver-tab-3'
        className={styles['grid-container']}>
        {tabIndex === '2' && <BrokerDataGrid onChange={onChange} />}
      </div>
      <div
        id='assgn-driver-tab-panel-4'
        role='tabpanel'
        hidden={tabIndex !== '3'}
        aria-labelledby='assgn-driver-tab-4'
        className={styles['grid-container']}>
        {tabIndex === '3' && <SalespersonTab onChange={onChange} />}
      </div>
    </>
  );
}

function SalespersonTab(props: { onChange: (value: unknown) => void }) {
  const { number: terminalNumber } = useCurrentTerminal();

  const { salesPersons, isLoading } = useFetchSalesPeopleQuery(
    {
      terminalNumber: terminalNumber
    },
    {
      selectFromResult: ({ data, ...rtkProps }) => ({
        salesPersons: data?.filter((x) => x.isActive) ?? [],
        ...rtkProps
      })
    }
  );

  const onSalespersonSelect = (selectedRow: SalesPerson) => {
    props.onChange({ driver: selectedRow, tractorId: null });
  };

  if (isLoading) return <ApplicationSpinner />;

  return (
    <SimpleDataGrid
      rows={salesPersons}
      columns={salepeopleColumnDefinition}
      selectOptions={{ onRowSelect: onSalespersonSelect }}
    />
  );
}
